import { useEffect, useState } from 'react';
import { useApiErrorToast } from '@/shared/ui/Toast';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/entities/session/model/authStore';
import { usePermissions } from '@/entities/rbac/hooks/usePermissions';
import { useDeploymentsList, useDeleteDeployment } from '@/features/deployments/hooks/useDeployments';
import { useThemeColors } from '@/shared/theme/useThemeColors';
import { semantic, typography, radius } from '@/shared/theme/tokens';
import { PageHeader } from '@/shared/ui/PageHeader';
import { PrimaryButton } from '@/shared/ui/PrimaryButton';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { SkeletonGrid } from '@/shared/ui/SkeletonCard';
import { ErrorState } from '@/shared/ui/ErrorState';
import { useModalStyles } from '@/shared/ui/modalStyles';
import { DeploymentFormModal } from './DeploymentFormModal';

export default function DeploymentsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { assignments } = useAuthStore();
  const { isRipikAdmin } = usePermissions();
  const { data: allDeployments, isLoading, isPlaceholderData, error } = useDeploymentsList();

  const [modal, setModal] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const toastApiError = useApiErrorToast();
  const deleteDeployment = useDeleteDeployment();

  const visibleDeployments = allDeployments
    ? isRipikAdmin
      ? allDeployments
      : allDeployments.filter((d) => assignments.some((a) => a.deploymentId === d.id))
    : [];

  function viewDeployment(dep) {
    navigate(`/deployments/${dep.id}/overview`);
  }

  useEffect(() => {
    if (isLoading || isPlaceholderData || !allDeployments) return;
    if (location.state?.noRedirect) return;
    if (visibleDeployments.length === 1) {
      viewDeployment(visibleDeployments[0]);
    }
    // Intentional: this auto-redirect must fire only when the deployment data
    // settles, not on every render. `visibleDeployments`/`viewDeployment` are
    // derived fresh each render; including them would re-run the redirect (or
    // loop). They are read here but deliberately excluded from the dep array.
  }, [allDeployments, isLoading, isPlaceholderData, assignments]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleDelete(deployment) {
    try {
      await deleteDeployment.mutateAsync(deployment.id);
      setConfirmDelete(null);
    } catch (err) { toastApiError(err); }
  }

  if (isLoading || isPlaceholderData) return <SkeletonGrid count={3} cardHeight={200} />;
  if (error) return <ErrorState message={error.message} />;
  if (!isPlaceholderData && visibleDeployments.length === 1 && !location.state?.noRedirect) return null;

  return (
    <div style={{ fontFamily: typography.fontFamily.body, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PageHeader
        title="Deployments"
        description={`${visibleDeployments.length} deployment${visibleDeployments.length !== 1 ? 's' : ''}`}
        actions={
          isRipikAdmin && (
            <PrimaryButton icon="add" onClick={() => setModal({ mode: 'create' })}>
              New Deployment
            </PrimaryButton>
          )
        }
      />

      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
      {visibleDeployments.length === 0 ? (
        <EmptyState onAdd={isRipikAdmin ? () => setModal({ mode: 'create' }) : null} />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))', gap: '16px' }}>
          {visibleDeployments.map((dep) => (
            <DeploymentCard
              key={dep.id}
              deployment={dep}
              canManage={isRipikAdmin}
              onDashboard={() => navigate(`/deployments/${dep.id}/overview`)}
              onEdit={() => setModal({ mode: 'edit', deployment: dep })}
              onDelete={() => setConfirmDelete(dep)}
            />
          ))}
        </div>
      )}
      </div>

      {modal && (
        <DeploymentFormModal
          mode={modal.mode}
          deployment={modal.deployment}
          onClose={() => setModal(null)}
        />
      )}

      {confirmDelete && (
        <ConfirmDeleteModal
          deployment={confirmDelete}
          loading={deleteDeployment.isPending}
          onConfirm={() => handleDelete(confirmDelete)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}

// ─── Deployment Card ──────────────────────────────────────────────────────────

function DeploymentCard({ deployment, canManage, onDashboard, onEdit, onDelete }) {
  const c = useThemeColors();
  const [hov, setHov] = useState(false);

  const cardStyle = {
    background: c.bg,
    borderRadius: radius['2xl'],
    border: `1px solid ${c.borderSubtle}`,
    boxShadow: hov ? '0 8px 24px rgba(30,67,150,0.12)' : '0 2px 8px rgba(30,67,150,0.06)',
    transform: hov ? 'translateY(-2px)' : 'none',
    overflow: 'hidden',
    transition: 'box-shadow 0.2s, transform 0.15s',
    display: 'flex',
    flexDirection: 'column',
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Open ${deployment.name} dashboard`}
      style={{ ...cardStyle, cursor: 'pointer' }}
      onClick={onDashboard}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onDashboard(); } }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
    >
      <div style={{ height: '3px', background: semantic.brandGrad, borderRadius: '14px 14px 0 0', overflow: 'hidden' }} />
      <div style={{ padding: '20px', flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
          <div style={{
            width: '36px', height: '36px', borderRadius: radius.lg,
            background: c.bgActiveGrad,
            border: `1px solid ${c.borderActive}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 20, color: c.textActive, fontVariationSettings: "'FILL' 1" }}>
              hub
            </span>
          </div>
          <StatusBadge status={deployment.status} />
        </div>
        <div style={{ marginBottom: '12px' }}>
          <h3 style={{ fontFamily: typography.fontFamily.heading, fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.bold, color: c.textPrimary, margin: '0 0 3px', letterSpacing: typography.letterSpacing.normal }}>
            {deployment.name}
          </h3>
          <p style={{ fontSize: typography.fontSize.base, color: c.textMuted, margin: 0 }}>{deployment.client}</p>
        </div>
        {deployment.plantType && (
          <div style={{ marginBottom: '14px' }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '5px',
              padding: '3px 10px', borderRadius: radius.sm,
              background: c.bgMuted, border: `1px solid ${c.borderSubtle}`,
              fontSize: typography.fontSize.sm, color: c.textLabel, fontWeight: typography.fontWeight.medium,
            }}>
              <span className="material-symbols-outlined" style={{ fontSize: 13, color: c.textSubtle }}>factory</span>
              {deployment.plantType}
            </span>
          </div>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '12px 14px', borderTop: `1px solid ${c.divider}`, background: c.bgSubtle, minWidth: 0, borderRadius: '0 0 14px 14px' }}>
        <button style={{
          flex: 1, minWidth: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
          padding: '10px 8px', minHeight: '40px', borderRadius: radius.md,
          background: semantic.btnPrimaryGrad, color: '#ffffff', border: 'none',
          fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.body, fontWeight: typography.fontWeight.semibold, cursor: 'pointer', letterSpacing: typography.letterSpacing.wide,
          overflow: 'hidden',
        }} onClick={onDashboard}>
          <span className="material-symbols-outlined" style={{ fontSize: 14, flexShrink: 0 }}>dashboard</span>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>View Deployment</span>
        </button>
        {canManage && (
          <>
            <button style={{
              width: '40px', height: '40px', borderRadius: radius.md, background: c.bg,
              border: `1px solid ${c.borderSubtle}`, display: 'flex', alignItems: 'center',
              justifyContent: 'center', cursor: 'pointer', padding: 0, flexShrink: 0,
            }} onClick={(e) => { e.stopPropagation(); onEdit(); }} title="Edit">
              <span className="material-symbols-outlined" style={{ fontSize: 15, color: c.textMuted }}>edit</span>
            </button>
            <button style={{
              width: '40px', height: '40px', borderRadius: radius.md, background: c.bg,
              border: `1px solid ${semantic.errorBorder}`, display: 'flex', alignItems: 'center',
              justifyContent: 'center', cursor: 'pointer', padding: 0, flexShrink: 0,
            }} onClick={(e) => { e.stopPropagation(); onDelete(); }} title="Delete">
              <span className="material-symbols-outlined" style={{ fontSize: 15, color: semantic.error }}>delete</span>
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Confirm Delete ───────────────────────────────────────────────────────────

function ConfirmDeleteModal({ deployment, loading, onConfirm, onCancel }) {
  const c = useThemeColors();
  const { overlayStyle, modalStyle } = useModalStyles('400px');
  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <div style={{ width: 40, height: 40, borderRadius: radius.lg, background: semantic.errorContainer, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span className="material-symbols-outlined" style={{ fontSize: 20, color: semantic.error }}>delete</span>
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.bold, color: c.textPrimary, fontFamily: typography.fontFamily.heading }}>
              Delete Deployment
            </h3>
            <p style={{ margin: '2px 0 0', fontSize: typography.fontSize.base, color: c.textMuted }}>This cannot be undone.</p>
          </div>
        </div>
        <p style={{ fontSize: typography.fontSize.lg, color: c.textLabel, margin: '0 0 20px', lineHeight: 1.5 }}>
          Are you sure you want to delete <strong>{deployment.name}</strong>?
        </p>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <PrimaryButton variant="secondary" onClick={onCancel} disabled={loading}>Cancel</PrimaryButton>
          <PrimaryButton variant="danger" onClick={onConfirm} disabled={loading} icon="delete">
            {loading ? 'Deleting…' : 'Delete'}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

// ─── Empty / Error ────────────────────────────────────────────────────────────

function EmptyState({ onAdd }) {
  const c = useThemeColors();
  return (
    <div style={{ textAlign: 'center', padding: '80px 24px', color: c.textMuted }}>
      <span className="material-symbols-outlined" style={{ fontSize: 48, color: c.borderInput, display: 'block', marginBottom: '16px' }}>hub</span>
      <h3 style={{ fontFamily: typography.fontFamily.heading, fontSize: typography.fontSize['2xl'], fontWeight: typography.fontWeight.bold, color: c.textLabel, margin: '0 0 8px' }}>
        No deployments yet
      </h3>
      <p style={{ fontSize: typography.fontSize.md, margin: '0 0 20px' }}>Create your first deployment to get started.</p>
      {onAdd && <PrimaryButton icon="add" onClick={onAdd}>New Deployment</PrimaryButton>}
    </div>
  );
}

